import { DEFAULT_VIEWER_PAGES as SHARED_DEFAULT_VIEWER_PAGES } from '@threadheads/shared';

/**
 * Single source of truth for sidebar nav pages, used by:
 *  - `AppSidebar` to render the nav (after role-based filtering)
 *  - `ViewerPagePicker` to render the per-user visibility editor for the
 *    Viewer role
 *  - The `viewer_visible_pages` column on `organization_memberships`
 *
 * Each page id maps directly to the `id` field on the corresponding NavItem
 * inside `AppSidebar`. Adding a new page = add an entry here AND wire up the
 * matching NavItem in the sidebar. Existing Viewer memberships will not see
 * the new page until an admin enables it via the picker.
 */
export type PageWorkspace = 'inventory' | 'production';

interface PageRegistryEntry {
  id: string;
  workspace: PageWorkspace;
  /** i18n key under the `nav` namespace. */
  labelKey: string;
}

export const PAGE_REGISTRY: PageRegistryEntry[] = [
  // Inventory workspace
  { id: 'inventory', workspace: 'inventory', labelKey: 'inventory.dashboard' },
  // BLU-711 A2. Registry membership IS the Viewer grant (see the note below);
  // the surface itself is read-only for a Viewer because every action is
  // gated on the Admin/Coordinator tier the backend enforces.
  { id: 'warehouse-positions', workspace: 'inventory', labelKey: 'inventory.positions' },
  { id: 'stocktakes', workspace: 'inventory', labelKey: 'inventory.stocktakes' },
  { id: 'products', workspace: 'inventory', labelKey: 'inventory.products' },
  { id: 'orders', workspace: 'inventory', labelKey: 'inventory.orders' },
  { id: 'transfers', workspace: 'inventory', labelKey: 'inventory.transfers' },
  { id: 'shipments', workspace: 'inventory', labelKey: 'inventory.shipments' },
  { id: 'suppliers', workspace: 'inventory', labelKey: 'inventory.suppliers' },
  { id: 'product-dev', workspace: 'inventory', labelKey: 'inventory.productDev' },
  { id: 'stock-alerts', workspace: 'inventory', labelKey: 'inventory.stockAlerts' },
  { id: 'stock-sync', workspace: 'inventory', labelKey: 'inventory.stockSync' },
  { id: 'consumables', workspace: 'inventory', labelKey: 'inventory.consumables' },
  { id: 'logs', workspace: 'inventory', labelKey: 'inventory.logs' },
  { id: 'reporting', workspace: 'inventory', labelKey: 'inventory.reporting' },
  { id: 'help', workspace: 'inventory', labelKey: 'footer.help' },
  // Production workspace.
  // NOTE (BLU-653): registry membership IS the Viewer grant — isItemVisible
  // (AppSidebar) bypasses the item's own `check` for Viewers, so adding a page
  // here is enough to make it Viewer-reachable regardless of its role gate
  // elsewhere. `product-sync` / `rules` / `cost-of-goods` and `prod-bridge`
  // are deliberately NOT registered: `prod-bridge`'s route carries
  // `RoleGuard allowedRoles={['Super Admin','Admin','Warehouse Coordinator']}`
  // (App.tsx), which excludes Viewer outright — registering it would only
  // produce a dead-end link. The other three are admin/manage surfaces whose
  // Viewer-grantability is a separate product decision, not a nav-regroup one.
  { id: 'prod-dashboard', workspace: 'production', labelKey: 'production.dashboard' },
  { id: 'prod-orders', workspace: 'production', labelKey: 'production.orders' },
  // BLU-1295 owner Client messages inbox (orders.view role set includes Viewer).
  { id: 'prod-client-messages', workspace: 'production', labelKey: 'production.clientMessages' },
  { id: 'prod-preprint', workspace: 'production', labelKey: 'production.preprint' },
  { id: 'prod-picker', workspace: 'production', labelKey: 'production.picker' },
  { id: 'prod-pretreat', workspace: 'production', labelKey: 'production.pretreat' },
  { id: 'prod-print', workspace: 'production', labelKey: 'production.printer' },
  { id: 'prod-film-print', workspace: 'production', labelKey: 'production.filmPrinting' },
  { id: 'prod-film-preprint', workspace: 'production', labelKey: 'production.filmPreprint' },
  { id: 'prod-pressing', workspace: 'production', labelKey: 'production.pressing' },
  { id: 'prod-catcher', workspace: 'production', labelKey: 'production.catcher' },
  { id: 'prod-packing', workspace: 'production', labelKey: 'production.packer' },
  { id: 'prod-bins', workspace: 'production', labelKey: 'production.bins' },
  { id: 'prod-film-storage', workspace: 'production', labelKey: 'production.filmStorage' },
  { id: 'prod-tracker', workspace: 'production', labelKey: 'production.tracker' },
  { id: 'prod-issues', workspace: 'production', labelKey: 'production.issues' },
  { id: 'prod-reports', workspace: 'production', labelKey: 'production.reports' },
];

/**
 * Default visibility for newly-created Viewer users.
 * Sourced from `@threadheads/shared` so the backend, the SQL backfill, and the
 * frontend all stay in lockstep.
 */
export const DEFAULT_VIEWER_PAGES: string[] = [...SHARED_DEFAULT_VIEWER_PAGES];

export function pagesByWorkspace(workspace: PageWorkspace): PageRegistryEntry[] {
  return PAGE_REGISTRY.filter((p) => p.workspace === workspace);
}

export function visibleWorkspaces(visiblePages: string[]): PageWorkspace[] {
  const set = new Set(visiblePages);
  const seen = new Set<PageWorkspace>();
  for (const page of PAGE_REGISTRY) {
    if (set.has(page.id)) seen.add(page.workspace);
  }
  return Array.from(seen);
}
